import type {
  PointerEvent as ReactPointerEvent,
  RefObject,
  TouchEvent as ReactTouchEvent,
} from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { pointFromClient } from './markupEditorCoordinates'
import {
  insertPolygonVertex,
  isPointInPage,
  polygonDraftAfterClick,
  polygonFromVertices,
  polygonValidationError,
  removePolygonVertex,
  replacePolygonVertex,
} from './markupEditorPolygon'
import {
  annotationGeometryEqual,
  annotationGeometryEquivalent,
  constrainRectangleTransition,
  containsRectangle,
  isValidRectangle,
  MIN_ANNOTATION_SIZE,
  rectangleFromPoints,
  rectangleMeetsMinimumSize,
  rectanglesOverlap,
} from './markupGeometry'
import type {
  AnnotationGeometry,
  MarkupAnnotation,
  MarkupEditorProps,
  MarkupPoint,
  MarkupPolygon,
  MarkupRectangle,
  MarkupTool,
} from './types'

export type EditorDraft =
  | { kind: 'idle' }
  | { kind: 'polygon'; vertices: MarkupPoint[]; interactionKey: string }
  | {
      kind: 'rectangle'
      pointerId: number
      start: MarkupPoint
      end: MarkupPoint
      interactionKey: string
    }
  | {
      kind: 'vertex'
      annotation: MarkupAnnotation & { geometry: MarkupPolygon }
      pointerId: number
      original: MarkupPolygon
      index: number
      preview: MarkupPolygon
      interactionKey: string
    }

const idleDraft: EditorDraft = { kind: 'idle' }
const pageBoundary: MarkupRectangle = {
  shape: 'rectangle',
  x_min: 0,
  y_min: 0,
  x_max: 1,
  y_max: 1,
}
const defaultRectangleMinimum = {
  width: MIN_ANNOTATION_SIZE,
  height: MIN_ANNOTATION_SIZE,
}

type GeometryUpdateResult = 'stale' | 'unchanged' | 'updated'

interface InteractionState {
  draft: EditorDraft
  tool: MarkupTool
  cancelRevision: number
  selectedVertex?: { annotationId: string; index: number }
}

interface InteractionOptions extends MarkupEditorProps {
  interactionResetKey: string
  drawingIntentKey: string
  pageRenderKey: string | undefined
  ready: boolean
  svgRef: RefObject<SVGSVGElement | null>
  reportError: (error?: string | { message: string }) => void
}

const isRndControl = (target: EventTarget | null) =>
  target instanceof Element &&
  Boolean(
    target.closest(
      '.markup-rnd-annotation .annotation-shape-handle, .markup-rnd-annotation [data-markup-resize-handle]'
    )
  )

export function useMarkupEditorInteraction(options: InteractionOptions) {
  const [state, setState] = useState<InteractionState>(() => ({
    draft: idleDraft,
    tool: options.drawingIntent?.tool ?? 'select',
    cancelRevision: 0,
  }))
  const consumedDrawingIntentKey = useRef<string>()
  if (options.drawingIntentKey === 'none')
    consumedDrawingIntentKey.current = undefined

  const key = JSON.stringify({
    context: options.interactionResetKey,
    tool: state.tool,
    ready: options.ready,
    pageRenderKey: options.pageRenderKey,
    cancelRevision: state.cancelRevision,
  })
  const live = useRef({ options, key, state })
  live.current = { options, key, state }

  const normalizedPoint = (
    event: Pick<PointerEvent, 'clientX' | 'clientY'>,
    clampToSurface = false
  ) => {
    const bounds = live.current.options.svgRef.current?.getBoundingClientRect()
    const point = bounds
      ? pointFromClient(event.clientX, event.clientY, bounds)
      : null
    if (!point) return null
    if (clampToSurface)
      return {
        x: Math.min(1, Math.max(0, point.x)),
        y: Math.min(1, Math.max(0, point.y)),
      }
    return isPointInPage(point) ? point : null
  }

  const updateState = useCallback((patch: Partial<InteractionState>) => {
    setState((current) => ({ ...current, ...patch }))
  }, [])

  const setDraft = useCallback(
    (draft: EditorDraft) => updateState({ draft }),
    [updateState]
  )
  const setTool = useCallback(
    (tool: MarkupTool) => updateState({ tool }),
    [updateState]
  )
  const setSelectedVertexValue = useCallback(
    (selectedVertex?: InteractionState['selectedVertex']) =>
      updateState({ selectedVertex }),
    [updateState]
  )
  const reportError = useCallback(
    (error?: string | { message: string }) =>
      live.current.options.reportError(error),
    []
  )

  const effectiveRectangleConstraints = () => {
    const constraints = live.current.options.rectangleConstraints
    return constraints
      ? { ...constraints, boundary: constraints.boundary ?? pageBoundary }
      : undefined
  }
  const rectangleMinimumSize = () =>
    live.current.options.rectangleConstraints
      ? (live.current.options.minimumRectangleSize ?? defaultRectangleMinimum)
      : defaultRectangleMinimum
  const constrainRectangle = (
    start: MarkupRectangle,
    candidate: MarkupRectangle
  ) => {
    const constraints = effectiveRectangleConstraints()
    return constraints
      ? constrainRectangleTransition(
          start,
          candidate,
          constraints,
          rectangleMinimumSize()
        )
      : candidate
  }
  const rectangleAllowed = (rectangle: MarkupRectangle) => {
    const constraints = effectiveRectangleConstraints()
    return Boolean(
      !constraints ||
      (isValidRectangle(rectangle) &&
        containsRectangle(constraints.boundary, rectangle) &&
        (constraints.obstacles ?? []).every(
          (obstacle) => !rectanglesOverlap(rectangle, obstacle)
        ) &&
        rectangleMeetsMinimumSize(rectangle, rectangleMinimumSize()))
    )
  }
  const rectangleForCreation = (start: MarkupPoint, end: MarkupPoint) => {
    const constraints = effectiveRectangleConstraints()
    const rectangle = rectangleFromPoints(
      start,
      end,
      live.current.options.minimumRectangleSize,
      { allowOutsidePage: Boolean(constraints) }
    )
    if (!rectangle || !constraints) return rectangle
    return constrainRectangleTransition(
      {
        shape: 'rectangle',
        x_min: start.x,
        y_min: start.y,
        x_max: start.x,
        y_max: start.y,
      },
      rectangle,
      constraints,
      rectangleMinimumSize()
    )
  }

  const activeDrawingIntent = () => {
    const current = live.current.options
    return current.drawingIntentKey === consumedDrawingIntentKey.current
      ? undefined
      : current.drawingIntent
  }

  const cancelGesture = useCallback(
    (
      cancelOptions: {
        preserveSelectedVertex?: boolean
        cancelLibraryInteraction?: boolean
      } = {}
    ) => {
      setState((current) => ({
        ...current,
        draft: idleDraft,
        cancelRevision:
          current.cancelRevision +
          Number(Boolean(cancelOptions.cancelLibraryInteraction)),
        ...(cancelOptions.preserveSelectedVertex
          ? {}
          : { selectedVertex: undefined }),
      }))
    },
    []
  )

  const annotationAccess = (annotation: MarkupAnnotation) => {
    const { options: current } = live.current
    const sameOwner = annotation.ownerId === current.selectedOwnerId
    const editable =
      sameOwner &&
      current.ready &&
      current.capabilities.canEdit &&
      !current.capabilities.readOnly
    const tool = state.tool
    return {
      sameOwner,
      canMove:
        editable &&
        (tool === 'select' ||
          tool === 'rectangle' ||
          (tool === 'point' &&
            annotation.geometry.shape === 'point' &&
            (current.repeatPointPlacement ?? false))),
      canEditControls:
        editable &&
        current.selectedAnnotationId === annotation.id &&
        (tool === 'select' || tool === 'rectangle'),
    }
  }

  const updateGeometry = (
    annotationId: string,
    original: AnnotationGeometry,
    next: AnnotationGeometry | undefined,
    interactionKey: string
  ): GeometryUpdateResult => {
    const current = live.current
    const annotation = current.options.annotations.find(
      (candidate) => candidate.id === annotationId
    )
    if (
      current.key !== interactionKey ||
      current.options.selectedAnnotationId !== annotationId ||
      !annotation ||
      !annotationGeometryEqual(annotation.geometry, original) ||
      !annotationAccess(annotation).canMove
    )
      return 'stale'
    if (!next || annotationGeometryEquivalent(original, next))
      return 'unchanged'
    if (original.shape !== next.shape) return 'stale'
    if (
      next.shape === 'rectangle' &&
      current.options.rectangleConstraints &&
      !rectangleAllowed(next)
    )
      return 'unchanged'
    live.current.state = { ...current.state, draft: idleDraft }
    setDraft(idleDraft)
    current.options.onUpdateAnnotation(annotationId, next)
    return 'updated'
  }

  const creationAllowed = (
    interactionKey: string,
    expectedTool: Exclude<MarkupTool, 'select'>
  ) => {
    const current = live.current
    const { options: context } = current
    const intent = activeDrawingIntent()
    return Boolean(
      current.key === interactionKey &&
      context.ready &&
      context.capabilities.canDraw &&
      !context.capabilities.readOnly &&
      state.tool === expectedTool &&
      intent?.tool === expectedTool &&
      intent.ownerId === context.selectedOwnerId &&
      intent.regionId === context.selectedRegionId &&
      intent.surfaceId === context.surface.id
    )
  }

  const submitCreation = (
    interactionKey: string,
    expectedTool: Exclude<MarkupTool, 'select'>,
    geometry: AnnotationGeometry,
    keepIntent = false
  ) => {
    const result =
      creationAllowed(interactionKey, expectedTool) &&
      (geometry.shape !== 'rectangle' || rectangleAllowed(geometry))
        ? live.current.options.onCreateAnnotation(geometry)
        : undefined
    if (!result || !('acceptedAnnotationId' in result)) {
      reportError(
        result && 'rejectedReason' in result
          ? { message: result.rejectedReason }
          : 'The annotation was not accepted.'
      )
      return false
    }
    if (!keepIntent) {
      consumedDrawingIntentKey.current = live.current.options.drawingIntentKey
      setTool('select')
    }
    reportError()
    return true
  }

  const submitDelete = (annotationId: string, interactionKey: string) => {
    const current = live.current
    const { options: context } = current
    const annotation = context.annotations.find(
      (candidate) => candidate.id === annotationId
    )
    if (
      current.key !== interactionKey ||
      !context.ready ||
      !context.capabilities.canDelete ||
      context.capabilities.readOnly ||
      context.selectedAnnotationId !== annotationId ||
      !annotation ||
      annotation.ownerId !== context.selectedOwnerId
    )
      return
    context.onDeleteAnnotation(annotationId)
  }

  const completePolygon = () => {
    const currentDraft = state.draft
    if (
      currentDraft.kind !== 'polygon' ||
      !creationAllowed(currentDraft.interactionKey, 'polygon')
    )
      return
    const polygon = polygonFromVertices(currentDraft.vertices)
    if (!polygon) {
      reportError(polygonValidationError(currentDraft.vertices))
      return
    }
    if (submitCreation(currentDraft.interactionKey, 'polygon', polygon))
      cancelGesture()
  }

  const addPolygonPoint = (point: MarkupPoint) => {
    const currentDraft = state.draft
    const vertices =
      currentDraft.kind === 'polygon' ? currentDraft.vertices : []
    const next = polygonDraftAfterClick(vertices, point)
    if (next.kind === 'complete') {
      completePolygon()
      return
    }
    if (next.kind === 'duplicate') {
      reportError('Polygon vertices must be distinct.')
      return
    }
    setDraft({
      kind: 'polygon',
      vertices: next.vertices,
      interactionKey: live.current.key,
    })
    reportError()
  }

  const transitionPointerGesture = (
    event: ReactPointerEvent<Element>,
    phase: 'move' | 'up'
  ) => {
    const pointerId = event.pointerId
    const gesture = state.draft
    const point = normalizedPoint(
      event,
      gesture.kind === 'rectangle' &&
        (live.current.options.clampRectangleToSurface ||
          Boolean(live.current.options.rectangleConstraints))
    )
    if (
      (gesture.kind !== 'vertex' && gesture.kind !== 'rectangle') ||
      gesture.pointerId !== pointerId
    )
      return

    if (gesture.kind === 'vertex') {
      if (
        updateGeometry(
          gesture.annotation.id,
          gesture.original,
          undefined,
          gesture.interactionKey
        ) === 'stale'
      ) {
        cancelGesture()
        return
      }
      if (phase === 'move') {
        const preview =
          point && replacePolygonVertex(gesture.original, gesture.index, point)
        if (preview) setDraft({ ...gesture, preview })
        return
      }
      const next =
        point && replacePolygonVertex(gesture.original, gesture.index, point)
      if (next && !annotationGeometryEqual(next, gesture.original)) {
        if (
          updateGeometry(
            gesture.annotation.id,
            gesture.original,
            next,
            gesture.interactionKey
          ) === 'stale'
        )
          reportError(
            'This annotation changed before the edit could be applied.'
          )
      }
      cancelGesture({ preserveSelectedVertex: true })
      return
    }

    if (!creationAllowed(gesture.interactionKey, 'rectangle')) {
      cancelGesture()
      return
    }
    if (phase === 'move') {
      if (point) setDraft({ ...gesture, end: point })
      return
    }
    const rectangle = point ? rectangleForCreation(gesture.start, point) : null
    if (rectangle)
      submitCreation(gesture.interactionKey, 'rectangle', rectangle)
    else reportError('Drag to create a positive rectangle inside the page.')
    cancelGesture()
  }

  const insertVertex = (
    annotation: MarkupAnnotation,
    edgeIndex: number,
    point: MarkupPoint,
    coordinateSpace: 'page' | 'client'
  ) => {
    if (annotation.geometry.shape !== 'polygon') return
    const bounds = live.current.options.svgRef.current?.getBoundingClientRect()
    const normalized =
      coordinateSpace === 'page'
        ? point
        : bounds
          ? pointFromClient(point.x, point.y, bounds)
          : null
    if (!normalized || !isPointInPage(normalized)) return
    const current = live.current
    const next = insertPolygonVertex(annotation.geometry, edgeIndex, normalized)
    const result = updateGeometry(
      annotation.id,
      annotation.geometry,
      next ?? undefined,
      current.key
    )
    if (result === 'stale') return
    if (next) {
      if (result !== 'updated') return
      setSelectedVertexValue({
        annotationId: annotation.id,
        index: edgeIndex + 1,
      })
      current.options.svgRef.current?.focus({ preventScroll: true })
    } else reportError('This change would make the polygon invalid.')
  }

  const focusAnnotation = (annotation: MarkupAnnotation, index?: number) => {
    setSelectedVertexValue(
      index === undefined ? undefined : { annotationId: annotation.id, index }
    )
    live.current.options.onSelectAnnotation(annotation.id)
  }

  const deleteSelectedVertex = () => {
    const current = live.current
    const target = state.selectedVertex
    const annotation = current.options.annotations.find(
      (candidate) => candidate.id === target?.annotationId
    )
    if (
      !target ||
      !annotation ||
      annotation.geometry.shape !== 'polygon' ||
      !annotationAccess(annotation).canEditControls
    )
      return false
    const next = removePolygonVertex(annotation.geometry, target.index)
    if (!next) {
      reportError('A polygon needs at least three distinct vertices.')
      return true
    }
    if (
      updateGeometry(annotation.id, annotation.geometry, next, current.key) ===
      'updated'
    ) {
      setSelectedVertexValue(undefined)
      current.options.svgRef.current?.focus({ preventScroll: true })
    }
    return true
  }

  const selectTool = (next: MarkupTool) => {
    const current = live.current.options
    const cancelDrawing =
      next === 'select' &&
      Boolean(current.guidedFlowActive || activeDrawingIntent())
    if (cancelDrawing)
      consumedDrawingIntentKey.current = current.drawingIntentKey
    cancelGesture({ cancelLibraryInteraction: true })
    setTool(next)
    reportError()
    if (cancelDrawing) current.onCancelDrawing?.()
  }

  const cancelDrawing = (notifyHost = true) => {
    consumedDrawingIntentKey.current = live.current.options.drawingIntentKey
    cancelGesture({ cancelLibraryInteraction: true })
    setTool('select')
    reportError('Drawing canceled.')
    if (notifyHost) live.current.options.onCancelDrawing?.()
  }

  const cancelFromKeyboard = () =>
    cancelDrawing(
      live.current.options.guidedFlowActive || Boolean(activeDrawingIntent())
    )

  const handleSurfacePointerDown = (
    event: ReactPointerEvent<SVGSVGElement>
  ) => {
    const currentDraft = state.draft
    const { capabilities } = live.current.options
    if (
      !live.current.options.ready ||
      currentDraft.kind === 'vertex' ||
      currentDraft.kind === 'rectangle' ||
      capabilities.readOnly
    )
      return
    if (
      currentDraft.kind === 'polygon' &&
      currentDraft.interactionKey !== live.current.key
    ) {
      cancelGesture()
      return
    }
    event.currentTarget.focus({ preventScroll: true })
    if (state.tool !== 'select' && !capabilities.tools.includes(state.tool)) {
      cancelGesture()
      setTool('select')
      return
    }
    if (
      state.tool !== 'select' &&
      !creationAllowed(live.current.key, state.tool)
    ) {
      if (!capabilities.canDraw)
        reportError(
          'Create or select an inspection item before drawing an annotation.'
        )
      return
    }
    const point = normalizedPoint(event)
    if (!point) return
    if (state.tool === 'select') {
      live.current.options.onSelectAnnotation(undefined)
      return
    }
    if (state.tool === 'point') {
      const current = live.current
      const accepted = submitCreation(
        current.key,
        'point',
        { shape: 'point', ...point },
        current.options.repeatPointPlacement ?? false
      )
      if (accepted && !current.options.repeatPointPlacement) cancelGesture()
      return
    }
    if (state.tool === 'rectangle') {
      event.currentTarget.setPointerCapture(event.pointerId)
      setDraft({
        kind: 'rectangle',
        pointerId: event.pointerId,
        start: point,
        end: point,
        interactionKey: live.current.key,
      })
      return
    }
    addPolygonPoint(point)
  }

  const handleVertexPointerDown = (
    event: ReactPointerEvent<SVGEllipseElement>,
    annotation: MarkupAnnotation,
    index: number
  ) => {
    event.stopPropagation()
    const focusCanSelect = event.currentTarget.getAttribute('tabindex') !== '-1'
    const focusChanged =
      focusCanSelect && document.activeElement !== event.currentTarget
    if (focusCanSelect) event.currentTarget.focus({ preventScroll: true })
    else options.svgRef.current?.focus({ preventScroll: true })
    const current = live.current
    const polygon = annotation.geometry
    const point = normalizedPoint(event)
    const started =
      polygon.shape === 'polygon' &&
      point &&
      annotationAccess(annotation).canMove
    if (started) {
      setDraft({
        kind: 'vertex',
        annotation: { ...annotation, geometry: polygon },
        pointerId: event.pointerId,
        original: polygon,
        index,
        preview: polygon,
        interactionKey: current.key,
      })
      if (!focusChanged) current.options.onSelectAnnotation(annotation.id)
      reportError()
    }
    setSelectedVertexValue({ annotationId: annotation.id, index })
    if (started) event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const cancelPointerGesture = (event: ReactPointerEvent<Element>) => {
    const cancelLibraryInteraction = isRndControl(event.target)
    const gesture = state.draft
    if (
      cancelLibraryInteraction ||
      ((gesture.kind === 'rectangle' || gesture.kind === 'vertex') &&
        gesture.pointerId === event.pointerId)
    )
      cancelGesture({ cancelLibraryInteraction })
  }

  const cancelTouchGesture = (event: ReactTouchEvent<Element>) => {
    if (isRndControl(event.target))
      cancelGesture({ cancelLibraryInteraction: true })
  }

  const previous = useRef({
    interactionKey: options.interactionResetKey,
    drawingIntentKey: options.drawingIntentKey,
    pageRenderKey: options.pageRenderKey,
    onCancelDrawing: options.onCancelDrawing,
  })
  useEffect(() => {
    const before = previous.current
    const interactionChanged =
      before.interactionKey !== options.interactionResetKey
    const intentChanged = before.drawingIntentKey !== options.drawingIntentKey
    const renderChanged = before.pageRenderKey !== options.pageRenderKey
    const cancelIntent = Boolean(
      interactionChanged &&
      !intentChanged &&
      before.drawingIntentKey !== 'none' &&
      consumedDrawingIntentKey.current !== before.drawingIntentKey
    )
    previous.current = {
      interactionKey: options.interactionResetKey,
      drawingIntentKey: options.drawingIntentKey,
      pageRenderKey: options.pageRenderKey,
      onCancelDrawing: options.onCancelDrawing,
    }
    if (interactionChanged && !intentChanged)
      consumedDrawingIntentKey.current = options.drawingIntentKey
    if (cancelIntent) {
      consumedDrawingIntentKey.current = before.drawingIntentKey
      before.onCancelDrawing?.()
    }
    const selected = live.current.state.selectedVertex
    const selectedChanged =
      selected && selected.annotationId !== options.selectedAnnotationId
    const wrongVertexDraft =
      live.current.state.draft.kind === 'vertex' &&
      live.current.state.draft.annotation.id !== options.selectedAnnotationId
    const contextChanged = interactionChanged || renderChanged
    if (contextChanged || wrongVertexDraft)
      cancelGesture({ cancelLibraryInteraction: renderChanged })
    else if (selectedChanged) setSelectedVertexValue(undefined)
    if (interactionChanged) reportError()
    if (interactionChanged) setTool('select')
    if (intentChanged) setTool(activeDrawingIntent()?.tool ?? 'select')
  }, [
    options.interactionResetKey,
    options.drawingIntentKey,
    options.pageRenderKey,
    options.selectedAnnotationId,
    options.onCancelDrawing,
    cancelGesture,
    reportError,
    setSelectedVertexValue,
    setTool,
  ])

  useEffect(() => {
    const draft = live.current.state.draft
    if (draft.kind !== 'vertex') return
    const current = options.annotations.find(
      (annotation) => annotation.id === draft.annotation.id
    )
    if (!current || !annotationGeometryEqual(current.geometry, draft.original))
      cancelGesture({ cancelLibraryInteraction: true })
  }, [options.annotations, cancelGesture])

  const { draft, tool, selectedVertex } = state
  const selectedVertexIndex =
    selectedVertex?.annotationId === options.selectedAnnotationId
      ? selectedVertex?.index
      : undefined
  return {
    key,
    tool,
    draft,
    selectedVertex: selectedVertexIndex,
    drawingIntent: activeDrawingIntent(),
    rectangleConstraints: options.rectangleConstraints,
    minimumRectangleSize: rectangleMinimumSize(),
    constrainRectangleTransition: constrainRectangle,
    rectangleForCreation,
    annotationAccess,
    submitDelete,
    updateGeometry,
    cancelGesture,
    selectTool,
    cancelDrawing,
    cancelFromKeyboard,
    completePolygon,
    transitionPointerGesture,
    handleSurfacePointerDown,
    handleVertexPointerDown,
    cancelPointerGesture,
    cancelTouchGesture,
    insertVertex,
    focusAnnotation,
    deleteSelectedVertex,
    selectAnnotation: (annotationId?: string) =>
      live.current.options.onSelectAnnotation(annotationId),
  }
}
